/**
 * Portal Manta · ApiStackPage
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { V, S } from '../../globals.js';
import { API, THl, TD, TDl } from '../../app-globals.js';
import { M } from '../../tokens/colors.js';

const ApiStackPage = memo(function ApiStackPage_Inner(){
  const [health,setHealth] = useState(null);
  const [carregando,setCarregando] = useState(false);
  async function checkHealth(){
    setCarregando(true);
    const r = await API.ping();
    setHealth(r||{erro:'backend não respondeu'});
    setCarregando(false);
  }
  useEffect(()=>{checkHealth();},[]);
  const grupos = [
    {nome:'Core (cross-módulo)',cor:V.bl,items:[
      {m:'GET',ep:'/api/health',d:'Status + contagens'},
      {m:'GET',ep:'/api/clients',d:'Lista clientes (perfil INV/CON/OUT)'},
      {m:'GET',ep:'/api/projects',d:'Lista projetos (filtros: modulo, status)'},
      {m:'GET',ep:'/api/dashboard/kpis',d:'Dashboard cross-módulo'},
    ]},
    {nome:'Rodovias',cor:V.or,items:[
      {m:'GET',ep:'/api/lotes',d:'Lista os 3 lotes'},
      {m:'GET',ep:'/api/lotes/:sig',d:'Detalhe completo'},
      {m:'GET',ep:'/api/lotes/:sig/sicro?disciplina=X',d:'Itens SICRO filtrados'},
    ]},
    {nome:'SICRO+',cor:V.gn,items:[
      {m:'GET',ep:'/api/sicro/desc/:cod',d:'Descrição SICRO'},
      {m:'GET',ep:'/api/sicro/composicao/:cod',d:'Insumos × coeficientes'},
      {m:'GET',ep:'/api/sicro/insumos/top',d:'Top insumos'},
    ]},
    {nome:'Propostas',cor:'#7c3aed',items:[
      {m:'GET',ep:'/api/proposals',d:'Lista (perfil/modulo/status)'},
      {m:'GET',ep:'/api/proposals/kpis',d:'KPIs por perfil'},
    ]},
    {nome:'KB · Orçamentos',cor:V.am||'#d97706',items:[
      {m:'GET',ep:'/api/kb',d:'Lista documentos curados'},
      {m:'POST',ep:'/api/orcamentos/calcular',d:'Calcula sem salvar'},
      {m:'POST',ep:'/api/orcamentos',d:'Calcula + salva'},
    ]},
    {nome:'Agentes IA',cor:'#db2777',items:[
      {m:'GET',ep:'/api/agentes',d:'Lista agentes'},
      {m:'POST',ep:'/api/agentes/leitor',d:'Extrai PDF (PER/MEF)'},
      {m:'POST',ep:'/api/agentes/orcamentista',d:'Orçamento via Claude'},
      {m:'POST',ep:'/api/agentes/validador',d:'Análise crítica'},
      {m:'GET',ep:'/api/diagnostico',d:'Validador determinístico'},
    ]},
  ];
  return(<div style={S.page}>
    <div style={S.h1}>API & Stack · Backend Manta Portal</div>
    <div style={S.sub}>Endpoints REST · base: <code>{API.base}</code> · alinhado ao Portal Manta Unificado</div>
    <div style={{...S.cardP,marginBottom:14,borderLeft:'4px solid '+(health?.ok?V.gn:V.or)}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:8}}>
        <span style={{fontSize:11.5,fontWeight:700,color:V.br}}>● Status</span>
        <button onClick={checkHealth} disabled={carregando} style={{padding:'4px 10px',fontSize:10,background:V.bl,color:'#fff',border:'none',borderRadius:3,cursor:'pointer'}}>{carregando?'…':'Recarregar'}</button>
        {health?.ok && <span style={{...S.pillBase,background:V.gn,color:'#fff'}}>ONLINE v{health.version}</span>}
        {!health?.ok && <span style={{...S.pillBase,background:V.or,color:'#fff'}}>OFFLINE</span>}
      </div>
      {health?.counts && <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:6}}>
        {Object.entries(health.counts).map(([k,v])=>(<div key={k} style={{padding:'5px 9px',background:V.g0,borderRadius:3,fontSize:10}}>
          <div style={{fontWeight:700,color:V.gr,fontSize:8.5,letterSpacing:.3}}>{k.toUpperCase()}</div>
          <div style={{fontWeight:700,color:V.br}}>{v}</div>
        </div>))}
      </div>}
    </div>
    {grupos.map(g=>(<div key={g.nome} style={{marginBottom:14}}>
      <div style={{fontSize:11,fontWeight:700,color:g.cor,letterSpacing:.4,marginBottom:6,paddingBottom:4,borderBottom:'2px solid '+g.cor}}>─ {g.nome.toUpperCase()}</div>
      <div style={{...S.card,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:10.5}}>
          <thead style={S.thRow}><tr style={{borderBottom:'1px solid '+V.b1}}>
            <th style={{...THl,width:60}}>Método</th>
            <th style={{...THl,width:280}}>Endpoint</th>
            <th style={THl}>Descrição</th>
          </tr></thead>
          <tbody>
            {g.items.map((it,i)=>(<tr key={i} style={S.trLine}>
              <td style={TD}><span style={{...S.pillBase,background:it.m==='GET'?V.gn:it.m==='POST'?V.bl:V.or,color:'#fff',padding:'1px 6px',fontSize:9}}>{it.m}</span></td>
              <td style={{...TDl,fontFamily:'ui-monospace,monospace',fontSize:10}}>{it.ep}</td>
              <td style={{...TDl,color:V.gr}}>{it.d}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>))}
    <div style={S.h2}>Stack tecnológico</div>
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
      {[
        {l:'Backend',v:'Node.js 18+ · Express',d:'API REST'},
        {l:'Banco',v:'SQLite (better-sqlite3)',d:'Schema 27 tabelas'},
        {l:'Frontend',v:'React (single-file)',d:'JSX puro'},
        {l:'Agentes IA',v:'Anthropic SDK',d:'claude-sonnet-4-6'},
        {l:'PDF parsing',v:'pdf-parse',d:'PER/MEF Vol II'},
        {l:'Tabelas core',v:'clients · projects · proposals · milestones',d:'Cross-módulo'},
      ].map(s=>(<div key={s.l} style={{...S.cardP,borderLeft:'3px solid '+V.bl}}>
        <div style={{fontSize:9.5,fontWeight:700,color:V.gr,letterSpacing:.3}}>{s.l}</div>
        <div style={{fontSize:11.5,fontWeight:700,color:V.br,marginTop:3}}>{s.v}</div>
        <div style={{fontSize:9.5,color:V.gr,marginTop:2}}>{s.d}</div>
      </div>))}
    </div>
  </div>);
});

export { ApiStackPage };
export default ApiStackPage;
